'use client';

import React from 'react';
import {
  Menu,
  X,
  ChevronDown,
  Sparkles,
  LogIn,
  User,
  LogOut,
  KeyRound,
  Moon,
  Sun,
} from 'lucide-react';
import { DarkModeToggle } from '~/components/DarkModeToggle';
import logoDark from '~/assets/images/logo-white.png';
import logoLight from '~/assets/images/logo-black.png';
import Image from 'next/image';
import { useAuth } from '~/hooks/useAuth';
import Link from 'next/link';
import { useTheme } from '~/hooks';

export function Navbar() {
  const { session, logOut } = useAuth();
  const isAuthenticated = session !== null;
  const { isDark, toggleDarkMode } = useTheme();

  const [isSolutionsOpen, setIsSolutionsOpen] = React.useState(false);
  const [isMobileSolutionsOpen, setIsMobileSolutionsOpen] =
    React.useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] =
    React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const onToggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="px-4 py-4 bg-card border-b border-card-border backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Center - Logo */}
        <Link
          href="/"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <Image
            src={isDark ? logoDark : logoLight}
            alt="Leadbunker"
            className="h-10 w-auto"
            width={300}
            height={100}
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            href="/about"
            className="text-foreground-secondary hover:text-foreground transition-colors"
          >
            About
          </Link>

          {/* Solutions Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setIsSolutionsOpen(true)}
            onMouseLeave={() => setIsSolutionsOpen(false)}
          >
            <Link
              href="/solutions"
              className="flex items-center gap-1 text-foreground-secondary hover:text-foreground transition-colors"
            >
              Solutions
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  isSolutionsOpen ? 'rotate-180' : ''
                }`}
              />
            </Link>

            {/* Dropdown Menu */}
            {isSolutionsOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-card border border-card-border rounded-lg shadow-xl py-2 z-50">
                <Link
                  href="/solutions/email-extraction"
                  className="w-full inline-block px-4 py-2 text-left text-foreground-secondary hover:text-foreground hover:bg-secondary transition-colors"
                >
                  Email Extraction
                </Link>
                <Link
                  href="/solutions/phone-extraction"
                  className="w-full inline-block px-4 py-2 text-left text-foreground-secondary hover:text-foreground hover:bg-secondary transition-colors"
                >
                  Phone Extraction
                </Link>
                <Link
                  href="/solutions/social-extraction"
                  className="w-full inline-block px-4 py-2 text-left text-foreground-secondary hover:text-foreground hover:bg-secondary transition-colors"
                >
                  Social Media Extraction
                </Link>
              </div>
            )}
          </div>

          <Link
            href="/pricing"
            className="text-foreground-secondary hover:text-foreground transition-colors"
          >
            Pricing
          </Link>

          <Link
            href="/contact"
            className="text-foreground-secondary hover:text-foreground transition-colors"
          >
            Contact
          </Link>

          <DarkModeToggle />
          {isAuthenticated ? (
            <>
              <div className="relative">
                <button
                  onClick={() =>
                    setIsProfileDropdownOpen(!isProfileDropdownOpen)
                  }
                  className="flex items-center gap-2 p-2 rounded-full hover:bg-secondary transition-colors"
                  aria-label="User menu"
                >
                  <div className="w-9 h-9 rounded-full bg-linear-to-br from-primary to-[#8b5cf6] flex items-center justify-center text-white shadow-lg">
                    <User className="w-5 h-5" />
                  </div>
                </button>

                {/* Profile Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-56 bg-card border border-card-border rounded-lg shadow-lg z-50 py-2">
                      <div className="px-4 py-3 border-b border-card-border">
                        <p className="text-foreground">John Doe</p>
                        <p className="text-foreground-secondary text-sm">
                          john.doe@example.com
                        </p>
                      </div>
                      <Link
                        href="/dashboard"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-foreground-secondary hover:bg-secondary hover:text-foreground transition-colors text-sm"
                      >
                        <User className="w-4 h-4" />
                        <span>Dashboard</span>
                      </Link>
                      <Link
                        href="/dashboard/settings?tab=security"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-foreground-secondary hover:bg-secondary hover:text-foreground transition-colors text-sm"
                      >
                        <KeyRound className="w-4 h-4" />
                        <span>Change Password</span>
                      </Link>
                      <button
                        onClick={() => {
                          toggleDarkMode();
                        }}
                        className="cursor-pointer w-full flex items-center gap-3 px-4 py-2.5 text-foreground-secondary hover:bg-secondary hover:text-foreground transition-colors text-sm"
                      >
                        {isDark ? (
                          <Sun className="w-4 h-4" />
                        ) : (
                          <Moon className="w-4 h-4" />
                        )}
                        <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                      </button>
                      <div className="border-t border-card-border my-2"></div>
                      <button
                        onClick={() => {
                          setIsProfileDropdownOpen(false);
                          logOut();
                        }}
                        className="w-full cursor-pointer flex items-center gap-3 px-4 py-2.5 text-red-500 hover:bg-secondary transition-colors text-sm"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                href="/auth/signin"
                className="text-foreground-secondary hover:text-foreground transition-colors flex items-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                Sign In
              </Link>
              <Link
                href="/auth/signup"
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity shadow-md flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={onToggleMobileMenu}
            className="p-2 text-foreground-secondary hover:text-foreground"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden mt-4 py-4 border-t border-card-border">
          <div className="flex flex-col gap-4">
            <Link
              href="/about"
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
              className="text-foreground-secondary hover:text-foreground transition-colors text-left"
            >
              About
            </Link>

            {/* Solutions Accordion */}
            <div>
              <button
                onClick={() => setIsMobileSolutionsOpen(!isMobileSolutionsOpen)}
                className="flex items-center justify-between w-full text-foreground-secondary hover:text-foreground transition-colors text-left"
              >
                Solutions
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isMobileSolutionsOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {isMobileSolutionsOpen && (
                <div className="ml-4 mt-2 flex flex-col gap-2">
                  <Link
                    href="/solutions/email-extraction"
                    onClick={() => {
                      setIsMobileSolutionsOpen(false);
                    }}
                    className="text-foreground-secondary hover:text-foreground transition-colors text-left"
                  >
                    Email Extraction
                  </Link>
                  <Link
                    href="/solutions/phone-extraction"
                    onClick={() => {
                      setIsMobileSolutionsOpen(false);
                    }}
                    className="text-foreground-secondary hover:text-foreground transition-colors text-left"
                  >
                    Phone Extraction
                  </Link>
                  <Link
                    href="/solutions/social-extraction"
                    onClick={() => {
                      setIsMobileSolutionsOpen(false);
                    }}
                    className="text-foreground-secondary hover:text-foreground transition-colors text-left"
                  >
                    Social Media Extraction
                  </Link>
                </div>
              )}
            </div>

            <Link
              href="/pricing"
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
              className="text-foreground-secondary hover:text-foreground transition-colors text-left"
            >
              Pricing
            </Link>

            <Link
              href="/contact"
              onClick={() => {
                setIsMobileMenuOpen(false);
              }}
              className="text-foreground-secondary hover:text-foreground transition-colors text-left"
            >
              Contact
            </Link>

            <div className="pt-4 border-t border-card-border flex flex-col gap-4">
              {isAuthenticated ? (
                <>
                  <Link
                    href="#"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      logOut();
                    }}
                    className="w-full px-4 py-2 bg-card/80 backdrop-blur-sm text-foreground border border-card-border rounded-lg hover:bg-card hover:shadow-lg transition-all duration-200 text-center shadow-md hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Log Out
                  </Link>
                  <Link
                    href="/dashboard"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity text-center shadow-md"
                  >
                    Go to Dashboard
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    href="/auth/signin"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 bg-card/80 backdrop-blur-sm text-foreground border border-card-border rounded-lg hover:bg-card hover:shadow-lg transition-all duration-200 text-center shadow-md hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/auth/signup"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity text-center shadow-md"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
